import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export function buildLeadSheetPDFBlob({ companyName, dotNumber, leadSheet, tractors, trailers, drivers, commodities }) {
  const ls = leadSheet || {};
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });

  const PAGE_W    = 612;
  const PAGE_H    = 792;
  const MARGIN    = 40;
  const CONTENT_W = PAGE_W - MARGIN * 2;

  let y = MARGIN;

  const checkPage = (needed = 20) => {
    if (y + needed > PAGE_H - MARGIN) {
      doc.addPage();
      y = MARGIN;
    }
  };

  const yn = v => v === 'yes' ? 'Yes' : v === 'no' ? 'No' : (v == null || v === '') ? '—' : String(v);

  // ── Header bar ──────────────────────────────────────────────────────────────
  doc.setFillColor(26, 35, 50);
  doc.rect(0, 0, PAGE_W, 58, 'F');

  doc.setFontSize(17);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('LEAD SHEET', MARGIN, 28);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(180, 195, 210);
  doc.text(companyName || 'Unknown Account', MARGIN, 46);
  const genDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  doc.text(`Generated: ${genDate}`, PAGE_W - MARGIN, 46, { align: 'right' });

  y = 74;

  // ── Section helper ──────────────────────────────────────────────────────────
  const section = (title, rows) => {
    if (!rows.some(r => r[1] && r[1] !== '—')) return; // skip empty sections
    checkPage(36);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(71, 85, 105);
    doc.text(title, MARGIN, y);
    y += 6;

    autoTable(doc, {
      startY: y,
      head:   [],
      body:   rows,
      margin: { left: MARGIN, right: MARGIN },
      styles: { fontSize: 8.5, cellPadding: 4, textColor: [55, 65, 81] },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 160, textColor: [71, 85, 105] },
        1: { cellWidth: CONTENT_W - 160 },
      },
      theme: 'plain',
      tableLineColor: [226, 232, 240],
      tableLineWidth: 0.5,
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });
    y = doc.lastAutoTable.finalY + 16;
  };

  // ── Company Information ─────────────────────────────────────────────────────
  section('COMPANY INFORMATION', [
    ['Company Name',      companyName || '—'],
    ['DOT Number',        dotNumber || '—'],
    ['FEIN',              ls.fein || '—'],
    ['Years in Business', ls.yearsInBusiness || '—'],
    ['Domicile State',    ls.state || '—'],
    ['Current Insurer',   ls.currentInsurer || '—'],
    ['Owner\'s Name',     ls.ownerName || '—'],
    ['Owner\'s DOB',      ls.ownerDob || '—'],
    ['Garaging Address',  ls.garagingAddress || '—'],
    ['Phone',             ls.phone || '—'],
    ['Current X-Date',    ls.currentXDate || '—'],
  ]);

  // ── Operations ──────────────────────────────────────────────────────────────
  const addingTrucksStr = ls.planningAddTrucks === 'yes'
    ? `Yes — ${ls.addTrucksCount || '?'} trucks`
    : yn(ls.planningAddTrucks);
  const nonRenewedStr = ls.beingNonRenewed === 'yes'
    ? `Yes — ${ls.nonRenewalReason || '?'}`
    : yn(ls.beingNonRenewed);
  const outOfStateStr = ls.driversOutOfState === 'yes'
    ? `Yes — ${ls.driversOutOfStateExplain || '?'}`
    : yn(ls.driversOutOfState);

  section('OPERATIONS', [
    ['Commodities Hauled',       commodities || ls.commodities || '—'],
    ['Operating Radius',         ls.radiusOfOperation || ls.radius || '—'],
    ['Operating States',         ls.operatingStates || '—'],
    ['20%+ Fleet Change',        yn(ls.fleetChangeLastYear)],
    ['Adding Trucks',            addingTrucksStr],
    ['Non-renewed / Canceled',   nonRenewedStr],
    ['Owner-Ops vs Company',     ls.ownerOpsOrCompany || '—'],
    ['Drivers Out of State',     outOfStateStr],
  ]);

  // ── Fleet ───────────────────────────────────────────────────────────────────
  const tractorCount = (tractors || []).filter(t => t?.year || t?.make).length;
  const trailerCount = (trailers || []).filter(t => t?.year || t?.make).length;
  const driverCount  = (drivers  || []).filter(d => d?.name).length;
  const eldStr = ls.eldsInstalled === 'yes' ? `Yes — ${ls.eldBrand || '?'}` : yn(ls.eldsInstalled);
  const dashStr = ls.dashcams === 'yes' ? `Yes — ${ls.dashcamBrand || '?'}` : yn(ls.dashcams);

  section('FLEET', [
    ['Tractors',      tractorCount ? String(tractorCount) : '—'],
    ['Trailers',      trailerCount ? String(trailerCount) : '—'],
    ['Drivers',       driverCount  ? String(driverCount)  : '—'],
    ['ELDs Installed',eldStr],
    ['Dashcams',      dashStr],
  ]);

  // ── Tractor list ─────────────────────────────────────────────────────────────
  const activeTractors = (tractors || []).filter(t => t?.year || t?.make || t?.vin);
  if (activeTractors.length > 0) {
    checkPage(36);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(71, 85, 105);
    doc.text('TRACTOR LIST', MARGIN, y);
    y += 6;

    autoTable(doc, {
      startY: y,
      head:   [['Year', 'Make', 'Model', 'VIN']],
      body:   activeTractors.map(t => [t.year || '—', t.make || '—', t.model || '—', t.vin || '—']),
      margin: { left: MARGIN, right: MARGIN },
      styles: { fontSize: 8, cellPadding: 3, textColor: [55, 65, 81] },
      headStyles: { fillColor: [241, 245, 249], textColor: [71, 85, 105], fontStyle: 'bold', fontSize: 8 },
      theme: 'plain',
      tableLineColor: [226, 232, 240],
      tableLineWidth: 0.5,
    });
    y = doc.lastAutoTable.finalY + 16;
  }

  // ── Driver list ──────────────────────────────────────────────────────────────
  const activeDrivers = (drivers || []).filter(d => d?.name);
  if (activeDrivers.length > 0) {
    checkPage(36);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(71, 85, 105);
    doc.text('DRIVER LIST', MARGIN, y);
    y += 6;

    autoTable(doc, {
      startY: y,
      head:   [['Name', 'DOB / Age', 'CDL Exp (yrs)', 'License State']],
      body:   activeDrivers.map(d => [
        d.name || '—',
        d.dob || (d.age ? String(d.age) : '—'),
        d.experience != null ? String(d.experience) : '—',
        d.licenseState || '—',
      ]),
      margin: { left: MARGIN, right: MARGIN },
      styles: { fontSize: 8, cellPadding: 3, textColor: [55, 65, 81] },
      headStyles: { fillColor: [241, 245, 249], textColor: [71, 85, 105], fontStyle: 'bold', fontSize: 8 },
      theme: 'plain',
      tableLineColor: [226, 232, 240],
      tableLineWidth: 0.5,
    });
    y = doc.lastAutoTable.finalY + 16;
  }

  // ── Safety ───────────────────────────────────────────────────────────────────
  section('SAFETY', [
    ['BASIC Alerts',           ls.basicAlerts != null ? String(ls.basicAlerts) : (ls.fmcsaAlerts != null ? String(ls.fmcsaAlerts) : '—')],
    ['DOT Safety Rating',      ls.dotRating || ls.dotSafetyRating || '—'],
    ['Corrective Action Plan', ls.correctiveActionPlan || '—'],
    ['Target / Expiring Price', ls.targetPricing || '—'],
  ]);

  // ── Load Agreements & Notes ──────────────────────────────────────────────────
  if (ls.loadAgreements || ls.additionalNotes) {
    checkPage(36);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(71, 85, 105);

    if (ls.loadAgreements) {
      doc.text('LOAD AGREEMENTS', MARGIN, y);
      y += 10;
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(55, 65, 81);
      const laLines = doc.splitTextToSize(ls.loadAgreements, CONTENT_W);
      laLines.forEach(line => { checkPage(13); doc.text(line, MARGIN, y); y += 13; });
      y += 10;
    }

    if (ls.additionalNotes) {
      checkPage(20);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(71, 85, 105);
      doc.text('ADDITIONAL NOTES', MARGIN, y);
      y += 10;
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(55, 65, 81);
      const anLines = doc.splitTextToSize(ls.additionalNotes, CONTENT_W);
      anLines.forEach(line => { checkPage(13); doc.text(line, MARGIN, y); y += 13; });
    }
  }

  return doc.output('blob');
}
